import React, { useEffect, useRef, useState } from "react";
import AmpcDetailApplicationForm from "./components/ApmcDetailApplicationForm";
import { useSelector } from "react-redux";
import { getAllAPMC } from "../../auth/services/apiCalling";
import { Option } from "../../application/CommonSelect";
import { checkNewApmcDetailApplication, getAllSpices, getApmcPurposes, newOneTimeDocUpload } from "../../auth/services/privateapicalling";
import { languageMapping } from "../../schemes/AllSchemes/GujaratScheme";
import { toast, ToastContainer } from "react-toastify";

export interface SelectOption {
  value: string;
  label: string;
}
/* ========= FORM DATA ========= */

export interface SubYard {
  name: string;
  date: string;
  address: string;
  doc: { fileName: string; docId: string } | null;
}
export interface FormData {
  registrationDoc?: { fileName: string; docId: string };
  registrationDate?: string;
  district?: string;
  taluka?: SelectOption[];
  numberOfVillages?: string;
  emailId?: string;
  chairmanName?: string;
  chairmanMobile?: string;
  secretaryName?: string;
  secretaryMobile?: string;
  numberOfFarmers?: string;
  numberOfTraders?: string;
  numberOfCommissionAgents?: string;
  numberOfCooperatives?: string;
  firstMeetingDate?: string;
  firstMeetingDoc?: { fileName: string; docId: string };
  unifiedLicense?: string;
  generalCommissionAgents?: string;
  otherLicense?: string;
  lastAuditDate?: string;
  auditReport?: { fileName: string; docId: string };
  byeLawsDoc?: { fileName: string; docId: string };
  mainYardName?: string;
  mainYardDate?: string;
  mainYardAddress?: string;
  mainYardDoc?: { fileName: string; docId: string };
  // subYardName?: string;
  // subYardDate?: string;
  // subYardAddress?: string;
  // subYardDoc?: { fileName: string; docId: string };
  tradingVegetablesFruits?: string;
  otherCommodities?: SelectOption[];
  approvedStaffScheduleDoc?: { fileName: string; docId: string };
  totalStaffApproved?: string;
  totalStaffRecruited?: string;
  hasCanteen?: string;
  hasGuestHouse?: string;
  infrastructureDetails?: SelectOption[];
  hasPrivateLand?: string;
  hasLeasedLand?: string;
  canteenSeatingCapacity?: string;
  noOfBeds?: string;
  landHectare?: string;
  landAre?: string;
  landSqMeter?: string;
  leaseLandHectare?: string;
  leaseLandAre?: string;
  leaseLandSqMeter?: string;
  leasePeriodYears?: string;
  vegetables?: SelectOption[];
  fruits?: SelectOption[];
  subYards?: SubYard[];
}

/* ========= FORM REFS ========= */
export type FormRefs = {
  registrationDoc: React.RefObject<HTMLInputElement>;
  registrationDate: React.RefObject<HTMLInputElement>;
  district: React.RefObject<HTMLSelectElement>;
  taluka: React.RefObject<HTMLSelectElement>;
  numberOfVillages: React.RefObject<HTMLInputElement>;
  emailId: React.RefObject<HTMLInputElement>;
  chairmanName: React.RefObject<HTMLInputElement>;
  chairmanMobile: React.RefObject<HTMLInputElement>;
  secretaryName: React.RefObject<HTMLInputElement>;
  secretaryMobile: React.RefObject<HTMLInputElement>;
  numberOfFarmers: React.RefObject<HTMLInputElement>;
  numberOfTraders: React.RefObject<HTMLInputElement>;
  numberOfCommissionAgents: React.RefObject<HTMLInputElement>;
  numberOfCooperatives: React.RefObject<HTMLInputElement>;
  firstMeetingDate: React.RefObject<HTMLInputElement>;
  firstMeetingDoc: React.RefObject<HTMLInputElement>;
  unifiedLicense: React.RefObject<HTMLInputElement>;
  generalCommissionAgents: React.RefObject<HTMLInputElement>;
  otherLicense: React.RefObject<HTMLInputElement>;
  lastAuditDate: React.RefObject<HTMLInputElement>;
  auditReport: React.RefObject<HTMLInputElement>;
  byeLawsDoc: React.RefObject<HTMLInputElement>;
  mainYardName: React.RefObject<HTMLInputElement>;
  mainYardDate: React.RefObject<HTMLInputElement>;
  mainYardAddress: React.RefObject<HTMLInputElement>;
  mainYardDoc: React.RefObject<HTMLInputElement>;
  // subYardName: React.RefObject<HTMLInputElement>;
  // subYardDate: React.RefObject<HTMLInputElement>;
  // subYardAddress: React.RefObject<HTMLInputElement>;
  // subYardDoc: React.RefObject<HTMLInputElement>;
  tradingVegetablesFruits: React.RefObject<HTMLInputElement>;
  otherCommodities: React.RefObject<HTMLSelectElement>;
  approvedStaffScheduleDoc: React.RefObject<HTMLInputElement>;
  totalStaffApproved: React.RefObject<HTMLInputElement>;
  totalStaffRecruited: React.RefObject<HTMLInputElement>;
  hasCanteen: React.RefObject<HTMLInputElement>;
  hasGuestHouse: React.RefObject<HTMLInputElement>;
  infrastructureDetails: React.RefObject<HTMLSelectElement>;
  hasPrivateLand: React.RefObject<HTMLInputElement>;
  hasLeasedLand: React.RefObject<HTMLInputElement>;
  canteenSeatingCapacity: React.RefObject<HTMLInputElement>;
  noOfBeds: React.RefObject<HTMLInputElement>;
  landHectare: React.RefObject<HTMLInputElement>;
  landAre: React.RefObject<HTMLInputElement>;
  landSqMeter: React.RefObject<HTMLInputElement>;
  leaseLandHectare: React.RefObject<HTMLInputElement>;
  leaseLandAre: React.RefObject<HTMLInputElement>;
  leaseLandSqMeter: React.RefObject<HTMLInputElement>;
  leasePeriodYears: React.RefObject<HTMLInputElement>;
  vegetables: React.RefObject<HTMLInputElement>;
  fruits: React.RefObject<HTMLInputElement>;
};

/* ========= REQUIRED FIELDS ========= */
export type RequiredField = { key: keyof FormData; message: string };

export const REQUIRED_FIELDS: RequiredField[] = [
  { key: "registrationDoc", message: "Registration document is required" },
  { key: "registrationDate", message: "Registration date is required" },
  { key: "district", message: "District is required" },
  { key: "taluka", message: "Taluka is required" },
  { key: "numberOfVillages", message: "Number of villages is required" },
  { key: "emailId", message: "Email ID is required" },
  { key: "chairmanName", message: "Chairman name is required" },
  { key: "chairmanMobile", message: "Chairman mobile is required" },
  { key: "secretaryName", message: "Secretary name is required" },
  { key: "secretaryMobile", message: "Secretary mobile is required" },
  { key: "numberOfFarmers", message: "Number of farmers is required" },
  { key: "numberOfTraders", message: "Number of traders is required" },
  {
    key: "numberOfCommissionAgents",
    message: "Number of commission agents is required",
  },
  {
    key: "numberOfCooperatives",
    message: "Number of cooperatives is required",
  },
  { key: "firstMeetingDate", message: "First meeting date is required" },
  { key: "firstMeetingDoc", message: "First meeting document is required" },
  { key: "unifiedLicense", message: "Unified license is required" },
  {
    key: "generalCommissionAgents",
    message: "General commission agents is required",
  },
  { key: "otherLicense", message: "Other license is required" },
  { key: "lastAuditDate", message: "Last audit date is required" },
  { key: "auditReport", message: "Audit report is required" },
  { key: "byeLawsDoc", message: "Bye laws document is required" },
  { key: "mainYardName", message: "Main yard name is required" },
  { key: "mainYardDate", message: "Main yard date is required" },
  { key: "mainYardAddress", message: "Main yard address is required" },
  { key: "mainYardDoc", message: "Main yard document is required" },
  // { key: "subYardName", message: "Sub yard name is required" },
  // { key: "subYardDate", message: "Sub yard date is required" },
  // { key: "subYardAddress", message: "Sub yard address is required" },
  // { key: "subYardDoc", message: "Sub yard document is required" },
  {
    key: "tradingVegetablesFruits",
    message: "Trading vegetables/fruits is required",
  },
  { key: "otherCommodities", message: "Other commodities is required" },
  {
    key: "approvedStaffScheduleDoc",
    message: "Approved staff schedule document is required",
  },
  { key: "totalStaffApproved", message: "Total staff approved is required" },
  {
    key: "totalStaffRecruited",
    message: "Total staff recruited is required",
  },
  { key: "hasCanteen", message: "Canteen selection is required" },
  { key: "hasGuestHouse", message: "Guest house selection is required" },
  {
    key: "infrastructureDetails",
    message: "Infrastructure details is required",
  },
  { key: "hasPrivateLand", message: "Private land selection is required" },
  { key: "hasLeasedLand", message: "Leased land selection is required" },
//   { key: "landHectare", message: "Land hectare is required" },
//   { key: "landAre", message: "Land are is required" },
//   { key: "landSqMeter", message: "Land square meter is required" },
//   { key: "leaseLandHectare", message: "Lease land hectare is required" },
//   { key: "leaseLandAre", message: "Lease land are is required" },
//   { key: "leaseLandSqMeter", message: "Lease land square meter is required" },
//   { key: "leasePeriodYears", message: "Lease period in years is required" },
//   { key: "vegetables", message:"At least one vegetable is required"},
//   { key: "fruits", message:"At least one fruit is required"}
];

const Index = () => {
  // const [formData, setFormData] = useState<FormData>({});
      const [formData, setFormData] = useState<FormData>({
    subYards: [{ name: '', date: '', address: '', doc: null }] // Initialize with one entry
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [talukaOptions , setTalukaOptions] = useState<Option[]>([]);
  const [spicesOptions, setSpicesOptions] = useState([]);
   const [loading , setLoading] = useState(false)
  const [rawPurposes, setRawPurposes] = useState([])

const [applicationId , setApplicationID] = useState<string | null>("");
 const [isAllowed,setIsAllowed] = useState(true);
  const userDetail = useSelector((state: any) => state.auth.userDetails);
  const token = useSelector((state:any)=>state.auth.token)
  const refs: FormRefs = {
    registrationDoc: useRef(null),
    registrationDate: useRef(null),
    district: useRef(null),
    taluka: useRef(null),
    numberOfVillages: useRef(null),
    emailId: useRef(null),
    chairmanName: useRef(null),
    chairmanMobile: useRef(null),
    secretaryName: useRef(null),
    secretaryMobile: useRef(null),
    numberOfFarmers: useRef(null),
    numberOfTraders: useRef(null),
    numberOfCommissionAgents: useRef(null),
    numberOfCooperatives: useRef(null),
    firstMeetingDate: useRef(null),
    firstMeetingDoc: useRef(null),
    unifiedLicense: useRef(null),
    generalCommissionAgents: useRef(null),
    otherLicense: useRef(null),
    lastAuditDate: useRef(null),
    auditReport: useRef(null),
    byeLawsDoc: useRef(null),
    mainYardName: useRef(null),
    mainYardDate: useRef(null),
    mainYardAddress: useRef(null),
    mainYardDoc: useRef(null),
    // subYardName: useRef(null),
    // subYardDate: useRef(null),
    // subYardAddress: useRef(null),
    // subYardDoc: useRef(null),
    tradingVegetablesFruits: useRef(null),
    otherCommodities: useRef(null),
    approvedStaffScheduleDoc: useRef(null),
    totalStaffApproved: useRef(null),
    totalStaffRecruited: useRef(null),
    hasCanteen: useRef(null),
    hasGuestHouse: useRef(null),
    infrastructureDetails: useRef(null),
    hasPrivateLand: useRef(null),
    hasLeasedLand: useRef(null),
    canteenSeatingCapacity: useRef(null),
    noOfBeds: useRef(null),
    landHectare: useRef(null),
    landAre: useRef(null),
    landSqMeter: useRef(null),
    leaseLandHectare: useRef(null),
    leaseLandAre: useRef(null),
    leaseLandSqMeter: useRef(null),
    leasePeriodYears: useRef(null),
    vegetables: useRef(null),
    fruits: useRef(null)
  };

 const checkNewApplication=async()=>{
  setLoading(true);
  try{
      const response=await checkNewApmcDetailApplication(token);
      if(response?.success){
   setApplicationID(response?.data?.applicationId)
      }else{
        setIsAllowed(false);
      }
  }catch{

  }finally{
    setLoading(false)
  }
 }
  //GET TALUKA API
  const fetchTaluka=async()=>{
    try{
 const response = await getAllAPMC(token , userDetail?.Department?.district_id,'' );
 if(response?.success){
 const talukas = response.data.map((t) => ({
          label: t.Taluka,
          value: t.Taluka,
        }));
        setTalukaOptions(talukas)
 }
    }catch{
  return [];
    }
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    subYardIndex?: number // Add optional index parameter
  ) => {
   
    const { name, files } = e.target;
    
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name;
      
      // Get existing docId based on whether it's a subYard or regular field
      let docId = null;
      if (subYardIndex !== undefined) {
        docId = formData.subYards?.[subYardIndex]?.doc || null;
      } else {
        docId = formData[fieldName] || null;
      }
      
      handleUpload(name, file, fieldName, docId, fileName, subYardIndex);
    }
  };
const handleUpload = async (
  documentName: string,
  file: any,
  fieldName: string,
  docId?: string | null,
  fileName?: string,
  subYardIndex?: number // Add optional index parameter
) => {
  const uploadFormData = new FormData();
  uploadFormData.append("file", file);
  uploadFormData.append("documentName", documentName);
  uploadFormData.append("fieldName", fieldName);
  uploadFormData.append("applicationId", applicationId);

  try {
    setLoading(true);
    const res = await newOneTimeDocUpload(token, uploadFormData, docId);
    
    if (res.success === false) {
      toast.error(res.message, {
        position: "top-right",
        style: { fontSize: "1.2rem", padding: "16px" }
      });
    } else if (res?.success && res?.docId) {
      // Update formData based on whether it's a subYard or regular field
      if (subYardIndex !== undefined) {
        setFormData((prev) => ({
          ...prev,
          subYards: prev.subYards?.map((yard, i) =>
            i === subYardIndex
              ? { ...yard, doc:  res.docId  }
              : yard
          ),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [fieldName]:  res.docId 
        }));
      }
      
      toast.success("File uploaded successfully", {
        position: "top-right",
        style: { fontSize: "1.2rem", padding: "16px" }
      });
    }
  } catch (error) {
    toast.error("File upload failed", {
      position: "top-right",
      style: { fontSize: "1.2rem", padding: "16px" }
    });
  }finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (userDetail?.Department?.district_id) {
    setFormData((prev) => ({
      ...prev,
      district: userDetail.Department.District,
    }));
  }
  fetchTaluka();
  handleSelectScheme("all")
  fetchspices()
  checkNewApplication()
}, [userDetail]);

//GET SPICES API 
const fetchspices=async()=>{
  try{
const response=await getAllSpices(token);
     
      const formatted = response.data.map(item => ({
        value: item.id,
        label: item.Spices,
      }));
     
      setSpicesOptions(formatted);
  }catch(err){

  }
}
 

const handleSelectScheme = async (scheme) => {
  try {
    const resp = await getApmcPurposes(scheme);
    const raw = resp?.purpose || []; 
    
    // Filter to only include ids up to 38, then transform
    const transformedPurposes = raw
      .filter(purpose => purpose.id <= 38)
      .map(purpose => ({
        value: purpose.id,
        label: purpose.subName
      }));
    
    setRawPurposes(transformedPurposes);
  } catch (error) {
 
  }
};




  return (
    <>
      {isAllowed ? (
        <>
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{background: "rgba(0,0,0,0.3)", zIndex: 1050}}>
            <div className="bg-white p-4 rounded shadow d-flex flex-column align-items-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Uploading...</span>
              </div>
              <div className="fw-bold">Uploading, please wait...</div>
            </div>
          </div>
        )}
        <AmpcDetailApplicationForm
          formData={formData}
          setFormData={setFormData}
          refs={refs}
          requiredFields={REQUIRED_FIELDS}
          errors={errors}
          setErrors={setErrors}
          touched={touched}
          handleFileChange={handleFileChange}
          setTouched={setTouched}
          talukaOptions={talukaOptions}
          spicesOptions={spicesOptions}
          rawPurposes={rawPurposes}
          applicationId={applicationId}
        />
        </>
      ) : (
        <div className="page-wrapper p-3 mt-5">
          <div className="d-flex flex-column  align-items-center justify-content-center">
            <div className="text-danger mt-5">You have already Filled Your Application</div>
          </div>
        </div>
      )}
      <ToastContainer position='top-right' autoClose={3000} />
    </>
  );
};

export default Index;